'use client';

import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@velocity-exchange/sdk';
import { useMemo } from 'react';
import {
	AccountHealth,
	MobileAndTabletAccountHealth,
} from 'src/components/GridSections/AccountHealth/AccountHealth';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import { MarginInfo as MarginInfoData } from 'src/stores/driftStoreTypes';
import { toNotional } from 'src/utils/formatting';
import React from 'react';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import ContentGuard from 'src/components/ContentGuard';

export type MarginDisplayInfo = {
	netUsdValue: string;
	totalCollateral: string;
	accountBalance: string;
	unrealisedPnl: string;
	freeCollateral: string;
	accountLeverage: string;
	marginRatio: string;
	unsettledPnl: string;
	unsettledPnlNum: number;
	maintenanceReq: string;
	maintenanceReqPct: string;
	initialReq: string;
	initialReqPct: string;
	totalQuoteInOrders: string;
	quoteInOrders: string;
	health: number;
};

const getMarginAccountDisplayInfo = (
	marginInfo: MarginInfoData | undefined,
	health: number
): MarginDisplayInfo | undefined => {
	const marginRatio = marginInfo
		? marginInfo.marginRatioPct === 0
			? '100'
			: marginInfo.marginRatioPct > 1000
				? '>1000'
				: marginInfo.marginRatioPct.toFixed(2)
		: '-';

	const unsettledPnlNum = BigNum.from(
		marginInfo?.totalUnsettledPnl ?? ZERO,
		QUOTE_PRECISION_EXP
	).toNum();

	return {
		health,
		netUsdValue: `$${BigNum.from(
			marginInfo?.netUsdValue ?? ZERO,
			QUOTE_PRECISION_EXP
		).toMillified()}`,
		totalCollateral: toNotional(
			BigNum.from(
				marginInfo?.totalMaintenanceCollateral ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
		accountBalance: toNotional(
			BigNum.from(marginInfo?.collateral ?? ZERO, QUOTE_PRECISION_EXP).toNum()
		),
		unrealisedPnl: toNotional(
			BigNum.from(
				marginInfo?.unrealisedPnL ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
		freeCollateral: toNotional(
			BigNum.from(
				marginInfo?.freeCollateral ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
		accountLeverage: `${marginInfo?.leverage.toFixed(2) ?? '0'}x`,
		marginRatio: `${marginRatio}%`,
		unsettledPnl: toNotional(unsettledPnlNum),
		unsettledPnlNum,
		maintenanceReq: toNotional(
			BigNum.from(
				marginInfo?.maintenanceReq ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
		maintenanceReqPct: marginInfo?.totalMaintenanceCollateral.eq(ZERO)
			? 'N/A'
			: `${(
					(BigNum.from(
						marginInfo?.maintenanceReq ?? ZERO,
						QUOTE_PRECISION_EXP
					).toNum() /
						BigNum.from(
							marginInfo?.totalMaintenanceCollateral ?? ZERO,
							QUOTE_PRECISION_EXP
						).toNum()) *
					100
				).toFixed(2)}%`,
		initialReq: toNotional(
			BigNum.from(marginInfo?.initialReq ?? ZERO, QUOTE_PRECISION_EXP).toNum()
		),
		initialReqPct: `${(
			(BigNum.from(
				marginInfo?.initialReq ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum() /
				BigNum.from(
					marginInfo?.totalCollateral ?? ZERO,
					QUOTE_PRECISION_EXP
				).toNum()) *
			100
		).toFixed(2)}%`,
		quoteInOrders: toNotional(
			BigNum.from(
				marginInfo?.quoteInOpenOrders ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
		totalQuoteInOrders: toNotional(
			BigNum.from(
				marginInfo?.quoteInOpenOrders ?? ZERO,
				QUOTE_PRECISION_EXP
			).toNum()
		),
	};
};

const verticalBreakpoint = 1000;

const MarginInfo = (props: { expanded?: boolean }) => {
	const marginInfo = useCurrentAccountMarginInfo();
	const connected = useWalletIsConnected();

	const userAccountIsReady = useUserAccountIsReady();
	const userClient = useDriftAccountStore((s) => {
		return s.accounts[s.currentUserKey ?? '']?.client;
	});
	const health =
		connected && userAccountIsReady && userClient
			? userClient.getHealth()
			: 100;

	const marginInfoDisplay = getMarginAccountDisplayInfo(marginInfo, health);

	// Attempted performance improvement : Only render new values when the actual string representations of the account margin info has changed
	const marginInfoToRender = useMemo(() => {
		return marginInfoDisplay;
	}, [
		marginInfoDisplay?.netUsdValue,
		marginInfoDisplay?.totalCollateral,
		marginInfoDisplay?.accountBalance,
		marginInfoDisplay?.unrealisedPnl,
		marginInfoDisplay?.freeCollateral,
		marginInfoDisplay?.accountLeverage,
		marginInfoDisplay?.marginRatio,
		marginInfoDisplay?.unsettledPnl,
		marginInfoDisplay?.unsettledPnlNum,
		marginInfoDisplay?.maintenanceReq,
		marginInfoDisplay?.maintenanceReqPct,
		marginInfoDisplay?.initialReq,
		marginInfoDisplay?.initialReqPct,
		marginInfoDisplay?.totalQuoteInOrders,
		marginInfoDisplay?.quoteInOrders,
		marginInfoDisplay?.health,
	]);

	if (!marginInfoToRender) return null;

	return !props.expanded ? (
		<div className="z-30 flex min-h-full w-full rounded-sm bg-container-bg lg:absolute">
			<AccountHealth marginInfoDisplay={marginInfoToRender} />
		</div>
	) : (
		<ContentGuard requireWallet>
			<div className="relative thin-scroll h-full overflow-auto rounded-sm bg-main-bg py-md">
				<div className="h-full">
					<MobileAndTabletAccountHealth />
				</div>
			</div>
		</ContentGuard>
	);
};

MarginInfo.verticalBreakpoint = verticalBreakpoint;
export default React.memo(MarginInfo);
